import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAllQuizzes } from "../api/quizAPI";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

export default function QuizList() {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const fetchQuizzes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllQuizzes();
            if (res.success) {
        setQuizzes(res.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch quizzes", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Runs on every navigation to this page including back from result
  useEffect(() => {
    fetchQuizzes();
  }, [location.key, location.state]); // ✅ watch both key AND state

  return (
    <Box
      backgroundColor={
        theme.palette.mode === "dark"
          ? theme.palette.background.default
          : "#FFFFFF"
      }
      m={2}
    >
      <Box p={{ xs: 2, md: 3 }}>

        {/* HEADER */}
        <Typography
          variant="h5"
          fontWeight={400}
          color={theme.palette.mode === "dark" ? "text.primary" : "#343C6A"}
        >
          Quizzer
        </Typography>

        <Typography variant="body2" color="text.secondary" mb={3} mt={1}>
          Test your knowledge and track your progress
        </Typography>

        {/* INFO PANEL */}
        <Card
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 2,
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.background.paper
                : "#F4F7FF",
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" mb={1} color="primary">
            Test Your Knowledge
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={3}>
            Attempt quizzes to evaluate your understanding. Each question is
            timed individually and must be answered before moving on.
          </Typography>

          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            gap={2}
          >
            {[
              { label: "Individual Timers", desc: "Each question has its own timer", bg: "#E0F2FE" },
              { label: "Auto Reset", desc: "Timer resets for next question", bg: "#ECFDF5" },
              { label: "Unlimited Attempts", desc: "Attempt as many times as you want", bg: "#F5F3FF" },
              { label: "No Refresh", desc: "Don't refresh during the quiz", bg: "#FFF7ED" },
            ].map((rule) => (
              <Box
                key={rule.label}
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.action.hover
                      : rule.bg,
                  border: "1px solid rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color={theme.palette.mode === "dark" ? "text.primary" : "inherit"}
                >
                  {rule.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {rule.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Card>

        {/* QUIZ LIST */}
        <Typography
          variant="h5"
          mb={2}
          color={theme.palette.mode === "dark" ? "text.primary" : "#343C6A"}
        >
          Available Quizzes
        </Typography>

        {loading && <Typography>Loading quizzes...</Typography>}

        {!loading && quizzes.length === 0 && (
          <Typography color="text.secondary">
            No quizzes available right now
          </Typography>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 2,
          }}
        >
          {quizzes.map((quiz) => {
            const times = quiz.questions?.map((q) => q.TIME || 0) || [];
            const minTime = times.length ? Math.min(...times) : 0;
            const maxTime = times.length ? Math.max(...times) : 0;
            const totalMinutes = Math.ceil(
              times.reduce((a, b) => a + b, 0) / 60
            );

            return (
              <Card
                key={quiz.id}
                sx={{
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.background.paper
                      : "#EFF2FE",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {quiz.title}
                    </Typography>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    📄 Questions: {quiz.questions?.length || 0}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mt={0.5}
                  >
                    🔁 Attempts: {quiz.attemptsUsed || 0}
                  </Typography>

                  <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                    <Chip
                      icon={<AccessTimeIcon />}
                      label={`${minTime}–${maxTime}s / question`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Total: ${totalMinutes} min`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>

                <Box p={2} pt={0}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate(`/quiz/${quiz.id}`)}
                  >
                    ▶ Start Quiz
                  </Button>
                </Box>
              </Card>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}